import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Calculator, BookOpen, LayoutGrid, Trophy, ChevronRight, Play, RotateCcw, Star, Medal, Crown } from 'lucide-react';
import api from '../../api';

// ─── MATH SPEED GAME ─────────────────────────────────────────────────────────
function MathSpeedGame({ band, onFinish }) {
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [q, setQ] = useState(null);
  const [input, setInput] = useState('');
  const [streak, setStreak] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  const genQ = useCallback(() => {
    const ops = ['+', '-', '×'];
    const op = ops[Math.floor(Math.random() * ops.length)];
    let a, b, ans;
    if (band === 'Primary 1-3') {
      a = Math.floor(Math.random() * 20) + 1;
      b = Math.floor(Math.random() * 20) + 1;
    } else if (band === 'Primary 4-6') {
      a = Math.floor(Math.random() * 50) + 10;
      b = Math.floor(Math.random() * 20) + 2;
    } else {
      a = Math.floor(Math.random() * 100) + 10;
      b = Math.floor(Math.random() * 30) + 2;
    }
    if (op === '+') ans = a + b;
    else if (op === '-') { if (b > a) [a, b] = [b, a]; ans = a - b; }
    else { a = Math.floor(Math.random() * 12) + 2; b = Math.floor(Math.random() * 12) + 2; ans = a * b; }
    setQ({ text: `${a} ${op} ${b} = ?`, ans: String(ans) });
    setInput('');
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [band]);

  useEffect(() => {
    genQ();
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(timerRef.current); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [genQ]);

  useEffect(() => { if (timeLeft === 0) onFinish(score); }, [timeLeft, score, onFinish]);

  const submit = () => {
    if (!q || timeLeft === 0) return;
    if (input.trim() === q.ans) {
      const pts = 10 + streak * 2;
      setScore(s => s + pts);
      setStreak(s => s + 1);
      setFeedback({ ok: true, msg: `+${pts}` });
    } else {
      setStreak(0);
      setFeedback({ ok: false, msg: `✗ ${q.ans}` });
    }
    setTimeout(() => { setFeedback(null); genQ(); }, 400);
  };

  const pct = (timeLeft / 60) * 100;
  const barColor = timeLeft > 30 ? 'bg-emerald-500' : timeLeft > 10 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="space-y-4 text-center">
      <div className="flex items-center justify-between">
        <div className="text-2xl font-black text-blue-700">{score} pts</div>
        <div className={`text-lg font-bold ${timeLeft <= 10 ? 'text-red-600 animate-pulse' : 'text-slate-700'}`}>{timeLeft}s</div>
        <div className="text-sm text-slate-500">Streak: {streak}</div>
      </div>
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full ${barColor} transition-all duration-1000`} style={{ width: `${pct}%` }} />
      </div>
      {q && (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 relative">
          <p className="text-3xl font-black text-slate-800 mb-4">{q.text}</p>
          {feedback && (
            <div className={`absolute top-3 right-3 font-bold text-lg ${feedback.ok ? 'text-emerald-600' : 'text-red-500'}`}>
              {feedback.msg}
            </div>
          )}
          <input
            ref={inputRef}
            type="number"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && submit()}
            className="w-28 text-center text-2xl font-bold border-2 border-blue-300 rounded-xl p-2 focus:outline-none focus:border-blue-600"
            data-testid="math-answer-input"
          />
          <button
            onClick={submit}
            className="ml-3 bg-blue-600 text-white px-5 py-2 rounded-xl font-bold hover:bg-blue-700 transition-colors"
            data-testid="math-submit-btn"
          >
            Go
          </button>
          <p className="text-xs text-slate-400 mt-2">Press Enter or click Go</p>
        </div>
      )}
    </div>
  );
}

// ─── WORD SCRAMBLE GAME ───────────────────────────────────────────────────────
const WORD_BANKS = {
  easy: ['apple', 'school', 'water', 'happy', 'table', 'chair', 'music', 'paper', 'clock', 'learn', 'smart', 'study', 'house', 'bread', 'light'],
  medium: ['nature', 'science', 'library', 'improve', 'journey', 'balance', 'explain', 'observe', 'gravity', 'polygon', 'decimal', 'compute', 'digital', 'formula', 'history'],
  hard: ['algebra', 'chemical', 'molecule', 'momentum', 'colonial', 'democracy', 'economics', 'parliament', 'hypothesis', 'polynomial', 'mitochondria', 'photosynthesis', 'electromagnetic', 'thermodynamics'],
};

function scramble(word) {
  let arr = word.split(''), attempts = 0;
  do { for (let i = arr.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i+1)); [arr[i], arr[j]] = [arr[j], arr[i]]; } attempts++; }
  while (arr.join('') === word && attempts < 20);
  return arr.join('');
}

function WordScrambleGame({ band, onFinish }) {
  const difficulty = band === 'Primary 1-3' ? 'easy' : band === 'Primary 4-6' ? 'medium' : 'hard';
  const words = WORD_BANKS[difficulty];
  const [idx, setIdx] = useState(0);
  const [scrambled, setScrambled] = useState(() => scramble(words[0]));
  const [input, setInput] = useState('');
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(90);
  const [feedback, setFeedback] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setTimeLeft(tl => { if (tl <= 1) { clearInterval(t); return 0; } return tl - 1; }), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { if (timeLeft === 0) onFinish(score); }, [timeLeft, score, onFinish]);

  const next = useCallback(() => {
    const ni = (idx + 1) % words.length;
    setIdx(ni);
    setScrambled(scramble(words[ni]));
    setInput('');
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [idx, words]);

  const check = () => {
    if (!timeLeft) return;
    if (input.trim().toLowerCase() === words[idx]) {
      setScore(s => s + 15);
      setFeedback({ ok: true });
    } else {
      setFeedback({ ok: false, correct: words[idx] });
    }
    setTimeout(() => { setFeedback(null); next(); }, 600);
  };

  return (
    <div className="space-y-4 text-center">
      <div className="flex items-center justify-between">
        <div className="text-2xl font-black text-emerald-700">{score} pts</div>
        <div className={`text-lg font-bold ${timeLeft <= 10 ? 'text-red-600 animate-pulse' : 'text-slate-700'}`}>{timeLeft}s</div>
        <div className="text-sm text-slate-500 capitalize">{difficulty}</div>
      </div>
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
        <div className="h-full bg-emerald-500 transition-all duration-1000" style={{ width: `${(timeLeft/90)*100}%` }} />
      </div>
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 relative">
        <p className="text-sm text-slate-500 mb-2">Unscramble this word:</p>
        <p className="text-4xl font-black text-slate-800 tracking-[0.3em] mb-4 uppercase">{scrambled}</p>
        {feedback && (
          <div className={`text-sm font-bold mb-2 ${feedback.ok ? 'text-emerald-600' : 'text-red-500'}`}>
            {feedback.ok ? 'Correct! +15' : `Answer: ${feedback.correct}`}
          </div>
        )}
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && check()}
          className="w-40 text-center text-xl font-bold border-2 border-emerald-300 rounded-xl p-2 focus:outline-none focus:border-emerald-600 uppercase"
          placeholder="Your answer"
          data-testid="word-scramble-input"
        />
        <div className="flex gap-2 justify-center mt-3">
          <button onClick={check} className="bg-emerald-600 text-white px-5 py-2 rounded-xl font-bold hover:bg-emerald-700 transition-colors" data-testid="word-scramble-submit">
            Check
          </button>
          <button onClick={next} className="bg-slate-200 text-slate-700 px-5 py-2 rounded-xl font-bold hover:bg-slate-300 transition-colors">
            Skip
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MEMORY MATCH GAME ────────────────────────────────────────────────────────
const PAIRS = [
  ['Photosynthesis', 'Light→Food'], ['Mitosis', 'Cell Division'], ['Algebra', 'Solve x'],
  ['Democracy', 'People Rule'], ['Gravity', 'Pull Force'], ['Osmosis', 'Water Flow'],
  ['Catalyst', 'Speed Up Rxn'], ['Ecosystem', 'Life System'],
];

function MemoryMatchGame({ onFinish }) {
  const [cards, setCards] = useState(() => {
    const arr = PAIRS.slice(0, 6).flatMap(([a, b]) => [
      { id: Math.random(), text: a, pairKey: a + b, flipped: false, matched: false },
      { id: Math.random(), text: b, pairKey: a + b, flipped: false, matched: false },
    ]);
    for (let i = arr.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i+1)); [arr[i], arr[j]] = [arr[j], arr[i]]; }
    return arr;
  });
  const [selected, setSelected] = useState([]);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(120);
  const [moves, setMoves] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTimeLeft(tl => { if (tl <= 1) { clearInterval(t); return 0; } return tl - 1; }), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { if (timeLeft === 0) onFinish(score); }, [timeLeft, score, onFinish]);

  const matchedCount = cards.filter(c => c.matched).length;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (matchedCount === cards.length && cards.length > 0) setTimeout(() => onFinish(score + timeLeft), 300); }, [matchedCount]);

  const flip = (card) => {
    if (card.flipped || card.matched || selected.length === 2) return;
    const newCards = cards.map(c => c.id === card.id ? { ...c, flipped: true } : c);
    const newSel = [...selected, card];
    setCards(newCards);
    setSelected(newSel);
    setMoves(m => m + 1);
    if (newSel.length === 2) {
      if (newSel[0].pairKey === newSel[1].pairKey) {
        setTimeout(() => {
          setCards(prev => prev.map(c => c.pairKey === newSel[0].pairKey ? { ...c, matched: true } : c));
          setScore(s => s + 20);
          setSelected([]);
        }, 500);
      } else {
        setTimeout(() => {
          setCards(prev => prev.map(c => (c.id === newSel[0].id || c.id === newSel[1].id) ? { ...c, flipped: false } : c));
          setSelected([]);
        }, 800);
      }
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-2xl font-black text-purple-700">{score} pts</div>
        <div className={`text-lg font-bold ${timeLeft <= 10 ? 'text-red-600 animate-pulse' : 'text-slate-700'}`}>{timeLeft}s</div>
        <div className="text-sm text-slate-500">Moves: {moves}</div>
      </div>
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
        <div className="h-full bg-purple-500 transition-all duration-1000" style={{ width: `${(timeLeft/120)*100}%` }} />
      </div>
      <div className="grid grid-cols-4 gap-2">
        {cards.map(card => (
          <button
            key={card.id}
            onClick={() => flip(card)}
            data-testid={`memory-card-${card.id}`}
            className={`h-16 rounded-xl font-bold text-xs transition-all duration-300 ${
              card.matched ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-300' :
              card.flipped ? 'bg-purple-100 text-purple-800 border-2 border-purple-400' :
              'bg-gradient-to-br from-purple-500 to-indigo-600 text-white border-2 border-purple-400 hover:scale-105 cursor-pointer'
            }`}
          >
            {(card.flipped || card.matched) ? card.text : '?'}
          </button>
        ))}
      </div>
      <p className="text-center text-xs text-slate-500">Match pairs to earn 20 pts each!</p>
    </div>
  );
}

// ─── LEADERBOARD ─────────────────────────────────────────────────────────────
function GameLeaderboard({ gameSlug }) {
  const [board, setBoard] = useState([]);
  const [scope, setScope] = useState('school');

  useEffect(() => {
    api.get(`/games/leaderboard?game_slug=${gameSlug}&scope=${scope}&limit=10`)
      .then(r => { if (r?.success) setBoard(r.data || []); });
  }, [gameSlug, scope]);

  return (
    <div className="mt-4">
      <div className="flex gap-2 mb-3">
        {['class', 'school', 'global'].map(s => (
          <button key={s} onClick={() => setScope(s)}
            className={`px-3 py-1 text-xs rounded-full font-semibold capitalize transition-colors ${scope === s ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid={`leaderboard-${s}`}
          >{s}</button>
        ))}
      </div>
      {board.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-3">No scores yet. Be the first!</p>
      ) : (
        <div className="space-y-1">
          {board.slice(0, 5).map((entry, i) => (
            <div key={i} className="flex items-center gap-2 py-1.5 px-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black ${i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-slate-300 text-white' : i === 2 ? 'bg-orange-400 text-white' : 'bg-slate-100 text-slate-600'}`}>
                {i+1}
              </span>
              <span className="flex-1 text-sm font-semibold text-slate-700 truncate">{entry.student_name}</span>
              <span className="text-sm font-bold text-blue-600">{entry.score}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── GAME CARD ────────────────────────────────────────────────────────────────
function GameCard({ game, band, onPlayComplete }) {
  const [playing, setPlaying] = useState(false);
  const [finalScore, setFinalScore] = useState(null);
  const ICONS = { calculator: Calculator, 'book-open': BookOpen, 'layout-grid': LayoutGrid };
  const Icon = ICONS[game.icon] || Trophy;

  const handleFinish = useCallback(async (score) => {
    setFinalScore(score);
    setPlaying(false);
    try {
      await api.post('/games/score', { game_id: game.id, game_slug: game.slug, score });
      onPlayComplete?.();
    } catch {}
  }, [game, onPlayComplete]);

  const renderGame = () => {
    if (game.slug === 'math-speed') return <MathSpeedGame band={band} onFinish={handleFinish} />;
    if (game.slug === 'word-scramble') return <WordScrambleGame band={band} onFinish={handleFinish} />;
    if (game.slug === 'memory-match') return <MemoryMatchGame onFinish={handleFinish} />;
    return <p>Game not available</p>;
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden" data-testid={`game-card-${game.slug}`}>
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ backgroundColor: game.color + '20' }}>
              <Icon className="w-5 h-5" style={{ color: game.color }} />
            </div>
            <div>
              <h3 className="font-bold text-slate-800">{game.name}</h3>
              <p className="text-xs text-slate-400 capitalize">{game.category}</p>
            </div>
          </div>
          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{game.duration_seconds}s</span>
        </div>
        <p className="text-sm text-slate-500 mb-4 line-clamp-2">{game.description}</p>

        {!playing && finalScore === null && (
          <button
            onClick={() => setPlaying(true)}
            className="w-full py-2.5 rounded-xl font-bold text-white flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
            style={{ backgroundColor: game.color }}
            data-testid={`play-${game.slug}`}
          >
            <Play className="w-4 h-4" /> Play Now
          </button>
        )}

        {playing && (
          <div>
            {renderGame()}
            <button onClick={() => handleFinish(0)} className="mt-3 text-xs text-slate-400 hover:text-red-500 w-full text-center">
              Quit game
            </button>
          </div>
        )}

        {!playing && finalScore !== null && (
          <div className="text-center space-y-3">
            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-4">
              <Trophy className="w-8 h-8 text-amber-500 mx-auto mb-1" />
              <p className="text-2xl font-black text-amber-700">{finalScore} pts</p>
              <p className="text-xs text-slate-500">Game complete!</p>
            </div>
            <button
              onClick={() => { setFinalScore(null); setPlaying(false); }}
              className="flex items-center gap-1.5 mx-auto text-sm font-semibold text-slate-600 hover:text-blue-600"
              data-testid={`replay-${game.slug}`}
            >
              <RotateCcw className="w-4 h-4" /> Play again
            </button>
          </div>
        )}
      </div>

      <div className="border-t border-slate-100 p-4 bg-slate-50/50">
        <GameLeaderboard gameSlug={game.slug} />
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function GamesPage() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moduleEnabled, setModuleEnabled] = useState(true);
  const [band, setBand] = useState('');
  const [refresh, setRefresh] = useState(0);

  const load = useCallback(() => {
    api.get('/games').then(r => {
      setLoading(false);
      if (r?.success === false && r?.module_enabled === false) { setModuleEnabled(false); return; }
      if (r?.success) {
        setGames(r.data || []);
        setBand(r.student_band || '');
      }
    });
  }, []);

  useEffect(() => { load(); }, [load, refresh]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
    </div>
  );

  if (!moduleEnabled) return (
    <div className="max-w-lg mx-auto text-center py-20 px-4">
      <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <LayoutGrid className="w-8 h-8 text-slate-400" />
      </div>
      <h2 className="text-xl font-bold text-slate-700 mb-2">Games Not Available</h2>
      <p className="text-slate-500">Your school has not enabled the Games module yet. Ask your admin to enable it.</p>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-black text-slate-800 flex items-center gap-2" data-testid="games-page-title">
          <LayoutGrid className="w-7 h-7 text-purple-500" /> Learning Games
        </h1>
        <p className="text-slate-500 mt-1">Play educational games, earn points, and climb the leaderboard!</p>
        {band && <span className="inline-block mt-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{band}</span>}
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Games Available', value: games.length, icon: LayoutGrid, color: 'text-purple-600' },
          { label: 'Max Score', value: '100 pts', icon: Star, color: 'text-amber-500' },
          { label: 'Compete', value: 'Global', icon: Medal, color: 'text-emerald-500' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl p-3 shadow-sm border border-slate-100 text-center">
            <s.icon className={`w-5 h-5 ${s.color} mx-auto mb-1`} />
            <p className="font-black text-slate-800 text-sm">{s.value}</p>
            <p className="text-xs text-slate-400">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Games grid */}
      {games.length === 0 ? (
        <div className="text-center py-16">
          <LayoutGrid className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No games available yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {games.map(g => (
            <GameCard key={g.id} game={g} band={band} onPlayComplete={() => setRefresh(r => r + 1)} />
          ))}
        </div>
      )}
    </div>
  );
}
